import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import NavigationHelper from 'layers/navigation/navigationHelper'
import { selectors as licenseSelectors } from 'store/licenseManager'
import ContentEntryDataGetter from 'layers/content/Hocs/ContentEntryDataGetter'
import withApiError from 'layers/errorHandling/apiError/component'
import {
  ERROR_TYPE_CONTENTFUL,
  SHOW_CONTENT_ON_401,
} from 'layers/errorHandling/apiError'
import { navigationEvent } from 'layers/navigation/store/operations'
import { operations as reportsOperations } from 'store/reportsManager'
import {
  operations as siteOperations,
  selectors as siteSelectors,
} from 'store/siteManager'
import {
  operations as ssapiOperations,
  selectors as ssapiSelectors,
} from 'store/ssapiManager'
import { ADMIN_DASHBOARD_ENTRY_ID } from 'utils/entryIds'
import { PLAYBOOK_STEP_MAP } from 'utils/adminExperienceHelpers'
import PlaybookSteps from './component'
import {
  ALL_STAFF_BUTTON_TEXT,
  FAMILIES_BUTTON_TEXT,
  GOALS_TEXT,
  SUMMARY_LINK_TEXT,
} from './constants'
import { fieldNames, goalNavigationData } from './data'
import { getK5DigitalFromPreferences } from 'utils/sitePreferenceHelpers'
import { isGoalDefined } from './goalHelpers'
import {
  findPlanPanel,
  getActiveStepFromCompletion,
  resolveAdminK8Site,
  resolveSitePlaybookData,
} from './playbookHelpers'
import usePlaybookModal from './usePlaybookModal'

export const PlaybookStepsContainer = ({
  classCreationGoalValue,
  error,
  familyLetterSent,
  fetchK8PlaybookData,
  fetchPublishFlows,
  fetchSiteUsers,
  goal1Date,
  goal1IsCompleted,
  goal2Date,
  goal2IsCompleted,
  goal3Date,
  goal3Text,
  goal3IsCompleted,
  goal4Date,
  goal4IsCompleted,
  goal4Text,
  goal5Date,
  goal5Text,
  goal5IsCompleted,
  goalsAreCompleted,
  goalsIsInDraft,
  hasAllStepsCompleted,
  implementationApproachIsCompleted,
  isFetching,
  isSingleSite,
  kickoffHosted,
  kickoffPrepared,
  lessonCompletionGoalIsDisabled,
  lessonCompletionGoalValue,
  linkDropdownDisplayTitle,
  linkMenu,
  navigationEvent,
  panelData,
  playbookData,
  programStartDate,
  purposeIsCompleted,
  purposeIsInDraft,
  showHeader,
  showMonitorViewOnly,
  siteId,
  siteUserStats,
  userEmail,
  userInvitationMethod,
}) => {
  const [activePanel, setActivePanel] = useState(null)
  const [activeStep, setActiveStep] = useState(0)
  const [selectedGoal, setSelectedGoal] = useState('goal 1')
  const [showSummary, setShowSummary] = useState(false)
  const [sitePlaybookData, setSitePlaybookData] = useState({})

  const {
    ModalComponent,
    closeModal,
    goalsOnlyMode,
    handleModalClose,
    isModalOpen,
    launchModal,
    openModalAtStep,
  } = usePlaybookModal({ isSingleSite, showMonitorViewOnly })

  const handleDropdownLink = useCallback(
    link => {
      const { download = null, openSameTab, url: optionUrlPath } = link || {}

      if (download) {
        navigationEvent(
          download?.file?.file?.url,
          NavigationHelper.types.DOWNLOAD_LINK,
        )
      }

      const type = openSameTab
        ? NavigationHelper.types.PUSH
        : NavigationHelper.types.NEW_TAB
      navigationEvent(optionUrlPath, type)
    },
    [navigationEvent],
  )

  useEffect(() => {
    if (siteId) {
      fetchPublishFlows(siteId)
    }
  }, [fetchPublishFlows, siteId])

  useEffect(() => {
    if (siteId && showMonitorViewOnly) {
      fetchSiteUsers(siteId)
    }
  }, [fetchSiteUsers, showMonitorViewOnly, siteId])

  useEffect(() => {
    if (showMonitorViewOnly && !error && !isFetching && !playbookData) {
      fetchK8PlaybookData(userEmail, siteId)
    }
  }, [
    error,
    fetchK8PlaybookData,
    isFetching,
    playbookData,
    showMonitorViewOnly,
    siteId,
    userEmail,
  ])

  useEffect(() => {
    if (panelData?.length > 0) {
      setActivePanel(findPlanPanel(panelData))
    }
  }, [panelData, selectedGoal])

  useEffect(() => {
    setSitePlaybookData(resolveSitePlaybookData({ playbookData, siteId }))
  }, [playbookData, siteId])

  useEffect(() => {
    if (
      goalsAreCompleted &&
      implementationApproachIsCompleted &&
      purposeIsCompleted
    ) {
      setShowSummary(true)
      setActiveStep(3)
    } else {
      setShowSummary(false)
      setActiveStep(
        getActiveStepFromCompletion({
          implementationApproachIsCompleted,
          goalsAreCompleted,
          purposeIsCompleted,
        }),
      )
    }
  }, [
    activeStep,
    familyLetterSent,
    goalsAreCompleted,
    implementationApproachIsCompleted,
    kickoffHosted,
    kickoffPrepared,
    purposeIsCompleted,
    showSummary,
    userInvitationMethod,
  ])

  useEffect(() => {
    if (showMonitorViewOnly) {
      setSelectedGoal(goalNavigationData[0].key)
    }
  }, [showMonitorViewOnly])

  useEffect(() => {
    if (
      selectedGoal &&
      !isGoalDefined({
        goalKey: selectedGoal,
        goal3IsCompleted,
        goal4IsCompleted,
        goal5IsCompleted,
      })
    ) {
      setSelectedGoal(goalNavigationData[0].key)
    }
  }, [goal3IsCompleted, goal4IsCompleted, goal5IsCompleted, selectedGoal])

  return (
    <PlaybookSteps
      activePanel={activePanel}
      activeStep={activeStep}
      classCreationGoalValue={classCreationGoalValue}
      goal1Date={goal1Date}
      goal1IsCompleted={goal1IsCompleted}
      goal2Date={goal2Date}
      goal2IsCompleted={goal2IsCompleted}
      goal3Date={goal3Date}
      goal3IsCompleted={goal3IsCompleted}
      goal3Text={goal3Text}
      goal4Date={goal4Date}
      goal4IsCompleted={goal4IsCompleted}
      goal4Text={goal4Text}
      goal5Date={goal5Date}
      goal5IsCompleted={goal5IsCompleted}
      goal5Text={goal5Text}
      goalsAreCompleted={goalsAreCompleted}
      goalsIsInDraft={goalsIsInDraft}
      goalsOnlyMode={goalsOnlyMode}
      hasAllStepsCompleted={hasAllStepsCompleted}
      implementationApproachIsCompleted={implementationApproachIsCompleted}
      isModalOpen={isModalOpen}
      isSingleSite={isSingleSite}
      lessonCompletionGoalIsDisabled={lessonCompletionGoalIsDisabled}
      lessonCompletionGoalValue={lessonCompletionGoalValue}
      linkDropdownDisplayTitle={linkDropdownDisplayTitle}
      linkMenu={linkMenu}
      modalComponent={ModalComponent}
      navigationEvent={navigationEvent}
      onCloseModal={closeModal}
      onDropdownLinkSelect={handleDropdownLink}
      onGoalSelect={setSelectedGoal}
      onModalClose={handleModalClose}
      onOpenAllStaffModal={() => openModalAtStep(3, ALL_STAFF_BUTTON_TEXT)}
      onOpenFamiliesModal={() => openModalAtStep(3, FAMILIES_BUTTON_TEXT)}
      onOpenGoalsModal={() => launchModal(GOALS_TEXT)}
      onOpenGoalsModalFromWarning={() =>
        launchModal(GOALS_TEXT, { goalsOnly: true })
      }
      onOpenStepModal={openModalAtStep}
      onOpenSummaryModal={() => openModalAtStep(3, SUMMARY_LINK_TEXT)}
      onStepClick={openModalAtStep}
      programStartDate={programStartDate}
      purposeIsCompleted={purposeIsCompleted}
      purposeIsInDraft={purposeIsInDraft}
      selectedGoal={selectedGoal}
      showHeader={showHeader}
      showMonitorViewOnly={showMonitorViewOnly}
      showSummary={showSummary}
      siteId={siteId}
      sitePlaybookData={sitePlaybookData}
      siteUserStats={siteUserStats}
    />
  )
}

PlaybookStepsContainer.propTypes = {
  classCreationGoalValue: PropTypes.number,
  error: PropTypes.object,
  familyLetterSent: PropTypes.bool,
  fetchK8PlaybookData: PropTypes.func,
  fetchPublishFlows: PropTypes.func,
  fetchSiteUsers: PropTypes.func,
  goal1Date: PropTypes.number,
  goal1IsCompleted: PropTypes.bool,
  goal2Date: PropTypes.number,
  goal2IsCompleted: PropTypes.bool,
  goal3Date: PropTypes.number,
  goal3IsCompleted: PropTypes.bool,
  goal3Text: PropTypes.string,
  goal4Date: PropTypes.number,
  goal4IsCompleted: PropTypes.bool,
  goal4Text: PropTypes.string,
  goal5Date: PropTypes.number,
  goal5IsCompleted: PropTypes.bool,
  goal5Text: PropTypes.string,
  goalsAreCompleted: PropTypes.bool,
  goalsIsInDraft: PropTypes.bool,
  hasAllStepsCompleted: PropTypes.bool,
  implementationApproachIsCompleted: PropTypes.bool,
  isFetching: PropTypes.bool,
  isSingleSite: PropTypes.bool,
  kickoffHosted: PropTypes.bool,
  kickoffPrepared: PropTypes.bool,
  lessonCompletionGoalIsDisabled: PropTypes.bool,
  lessonCompletionGoalValue: PropTypes.number,
  linkDropdownDisplayTitle: PropTypes.string,
  linkMenu: PropTypes.array,
  navigationEvent: PropTypes.func,
  panelData: PropTypes.array,
  playbookData: PropTypes.object,
  programStartDate: PropTypes.number,
  purposeIsCompleted: PropTypes.bool,
  purposeIsInDraft: PropTypes.bool,
  showHeader: PropTypes.bool,
  showMonitorViewOnly: PropTypes.bool,
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  siteName: PropTypes.string,
  siteUserStats: PropTypes.shape({
    totalUsers: PropTypes.number,
    usersWithoutClasses: PropTypes.number,
    usersWithoutAccounts: PropTypes.number,
  }),
  stateDropdownOption: PropTypes.object,
  userEmail: PropTypes.string,
  userInvitationMethod: PropTypes.string,
}

export const mapStateToProps = (state, ownProps) => {
  const { contentful } = state || {}
  const { reportsManager, userProfileManager, userSessionManager } = state || {}
  const {
    admin: {
      k8: { isFetching, playbookData },
    },
    error,
  } = reportsManager || {}
  const {
    profile: { email: userEmail },
  } = userProfileManager || {}
  const {
    adminDashboard: {
      k8: { playbookStepsDropdown },
    },
  } = userSessionManager || {}
  const panelData =
    contentful?.[ADMIN_DASHBOARD_ENTRY_ID]?.payload?.leaderActivationPanel1 ||
    []
  const { navigationEvent } = ownProps || {}
  const adminK8Sites = licenseSelectors.selectActiveAdminK8Sites(state)
  const site = resolveAdminK8Site({
    adminK8Sites,
    site: ownProps?.site,
    siteId: ownProps?.siteId,
  })
  const siteId = ownProps?.siteId || site.id
  const preferences = ownProps?.preferences ?? site?.preferences
  const k5Digital = getK5DigitalFromPreferences(preferences)
  const {
    classCreationGoalDate,
    classCreationGoalValue,
    lessonCompletionGoalDate,
    lessonCompletionGoalValue,
    goal3Date,
    goal3Text,
    goal4Date,
    goal4Text,
    goal5Date,
    goal5Text,
  } = k5Digital || {}
  const stateDropdownOption = playbookStepsDropdown?.[site.id] || null
  const publishFlows = ssapiSelectors.selectPublishFlowsBySiteId(state, siteId)
  const publishFlow =
    publishFlows?.find(flow => flow.name === PLAYBOOK_STEP_MAP.name) || {}
  const publishFlowData = {}

  fieldNames.forEach(fieldName => {
    publishFlowData[fieldName] =
      publishFlow?.publishFlowItems?.find(item => item.name === fieldName)
        ?.completeValue || null
  })
  const {
    CounselorLed: counselorLed,
    FamilyLetterSent: familyLetterSent,
    ImplementationType: implementationType,
    KickoffHosted: kickoffHosted,
    KickoffPrepared: kickoffPrepared,
    Purpose: purpose,
    TeacherLed: teacherLed,
    UserInvitationMethod: userInvitationMethod,
  } = publishFlowData || {}
  const programStartDate = k5Digital?.programStartDate ?? null
  const lessonCompletionGoalIsDisabled =
    k5Digital?.lessonCompletionGoalIsDisabled
  const goal1IsCompleted = !!classCreationGoalDate && !!classCreationGoalValue
  const goal2IsCompleted =
    !!lessonCompletionGoalDate && !!lessonCompletionGoalValue
  const goal3IsCompleted =
    !!goal3Date && !!goal3Text && String(goal3Text).trim().length > 0
  const goal4IsCompleted =
    !!goal4Date && !!goal4Text && String(goal4Text).trim().length > 0
  const goal5IsCompleted =
    !!goal5Date && !!goal5Text && String(goal5Text).trim().length > 0

  const goalsAreCompleted = lessonCompletionGoalIsDisabled
    ? goal1IsCompleted
    : (goal1IsCompleted && goal2IsCompleted) || false
  const goalsIsInDraft =
    !goalsAreCompleted &&
    (classCreationGoalDate ||
      classCreationGoalValue ||
      goal3Date ||
      goal3Text ||
      goal4Date ||
      goal4Text ||
      goal5Date ||
      goal5Text ||
      lessonCompletionGoalDate ||
      lessonCompletionGoalValue ||
      programStartDate)

  // Check completion using flags first (if all flags are set)
  const hasAllCompletionFlags =
    !!implementationType && (teacherLed || counselorLed)

  // Fallback: check if values exist (for backward compatibility with data that wasn't saved with flags)
  // For programIsEnabled, check that it's explicitly set (not null), not just truthy
  const hasAllValues = !!implementationType && (teacherLed || counselorLed)

  // More lenient check: if implementationType and leader are completed,
  // consider it complete even if programIsEnabledIsCompleted is missing
  // (as long as programIsEnabled is explicitly set - false is valid)
  const hasCoreCompletion =
    !!implementationType &&
    implementationType !== null &&
    (teacherLed || counselorLed)

  const implementationApproachIsCompleted =
    hasAllCompletionFlags || hasAllValues || hasCoreCompletion || false
  const purposeIsInDraft = purpose === null
  const purposeIsCompleted = purpose?.length > 0
  const hasAllStepsCompleted =
    goalsAreCompleted && implementationApproachIsCompleted && purposeIsCompleted

  return {
    classCreationGoalValue,
    error,
    familyLetterSent,
    goalsAreCompleted,
    goal1Date: classCreationGoalDate,
    goal1IsCompleted,
    goal2Date: lessonCompletionGoalDate,
    goal2IsCompleted,
    goal3Date,
    goal3Text,
    goal3IsCompleted,
    goal4Date,
    goal4IsCompleted,
    goal4Text,
    goal5Date,
    goal5IsCompleted,
    goal5Text,
    goalsIsInDraft,
    hasAllStepsCompleted,
    implementationApproachIsCompleted,
    isFetching,
    isSingleSite:
      ownProps.isSingleSite ??
      (Array.isArray(adminK8Sites) && adminK8Sites.length === 1),
    kickoffHosted,
    kickoffPrepared,
    lessonCompletionGoalIsDisabled,
    lessonCompletionGoalValue,
    navigationEvent,
    panelData,
    playbookData,
    programStartDate,
    purposeIsCompleted,
    purposeIsInDraft,
    siteId,
    siteName: site.name,
    siteUserStats: siteSelectors.selectPlaybookGoalUserStats(state, siteId),
    stateDropdownOption,
    userEmail,
    userInvitationMethod,
  }
}

const mapDispatchToProps = {
  navigationEvent,
  fetchK8PlaybookData: reportsOperations.fetchK8PlaybookData,
  fetchPublishFlows: ssapiOperations.fetchPublishFlows,
  fetchSiteUsers: siteOperations.fetchSiteUsers,
}

const mapper = entry => {
  const { displayTitle, linkMenu } = entry || {}
  return {
    linkDropdownDisplayTitle: displayTitle,
    linkMenu,
  }
}

const options = {
  entryId: '7CIaQNOLCEQvJMd8GHkCeP',
  include: 3,
  mapper,
  spread: true,
}

export default ContentEntryDataGetter(
  withApiError(
    PlaybookStepsContainer,
    [ERROR_TYPE_CONTENTFUL],
    [SHOW_CONTENT_ON_401],
  ),
  options,
  connect(mapStateToProps, mapDispatchToProps),
)
